import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListSubheader,
  IconButton,
  Paper,
  Fade,
} from '@mui/material'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import RefreshIcon from '@mui/icons-material/Refresh'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { useTts } from '../../contexts/TtsContext'
import { synthesize } from '../../services/ttsApi'
import { useAuth } from '../../App'
import TtsAudioPlayer from './TtsAudioPlayer'
import { useSimpleRecorder, FileUploader } from './SimpleRecorder'
import { groupVoicesByLanguage } from '../../types/tts'
import { tokens } from '../../theme/tokens'

const API_BASE = (window as any).__ENV__?.API_BASE || 'http://localhost:3000'

// M17: supported languages for transcription
const LANGUAGES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'fr-FR', label: 'French' },
  { code: 'de-DE', label: 'German' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'it-IT', label: 'Italian' },
]

const RoundTripDemo: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { voices, loadVoices, checkTtsHealth } = useTts()
  const auth = useAuth()
  const recorder = useSimpleRecorder()

  const [activeStep, setActiveStep] = useState(0)

  // Stage 0: original audio
  const [originalBlob, setOriginalBlob] = useState<Blob | null>(null)
  const [originalURL, setOriginalURL] = useState<string | null>(null)

  // M17: language selector state -- default to fr-FR
  const [transcribeLang, setTranscribeLang] = useState('fr-FR')

  // Stage 1: transcription
  const [transcribing, setTranscribing] = useState(false)
  const [transcribeError, setTranscribeError] = useState<string | null>(null)

  // M18: elapsed time counter during transcription
  const [elapsedMs, setElapsedMs] = useState(0)
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Stage 2: edit
  const [editedText, setEditedText] = useState('')

  // Stage 3: TTS
  const [selectedVoiceId, setSelectedVoiceId] = useState('')
  const [ttsBlob, setTtsBlob] = useState<Blob | null>(null)
  const [ttsLoading, setTtsLoading] = useState(false)
  const [ttsError, setTtsError] = useState<string | null>(null)

  const steps = [
    t('tts.step1Record', 'Record'),
    t('tts.step2Transcribe', 'Transcribe'),
    t('tts.step3Edit', 'Edit'),
    t('tts.step4Speak', 'Speak'),
  ]

  // Accent colors per stage phase -- ASR (cyan) for stages 0-1, TTS (dark teal) for stages 2-3
  const isAsrPhase = activeStep <= 1
  const accentColor = isAsrPhase
    ? (isDark ? '#00CFF3' : '#007d8a')
    : (isDark ? tokens.colors.tts.light : tokens.colors.tts.main)
  const accentBg = isAsrPhase
    ? (isDark ? 'rgba(0, 207, 243, 0.08)' : 'rgba(0, 125, 138, 0.06)')
    : (isDark ? 'rgba(0, 74, 87, 0.08)' : 'rgba(0, 74, 87, 0.05)')
  const accentBorder = isAsrPhase
    ? (isDark ? 'rgba(0, 207, 243, 0.2)' : 'rgba(0, 125, 138, 0.18)')
    : (isDark ? 'rgba(0, 74, 87, 0.2)' : 'rgba(0, 74, 87, 0.18)')

  // Load voices on mount
  useEffect(() => {
    const init = async () => {
      try {
        const token = await auth.getToken()
        await checkTtsHealth()
        await loadVoices(token)
      } catch {
        // handled by auth context
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pre-select first voice when voices load
  useEffect(() => {
    if (voices.length > 0 && !selectedVoiceId) {
      setSelectedVoiceId(voices[0].voice_id)
    }
  }, [voices, selectedVoiceId])

  // When recorder captures audio, set it as original
  useEffect(() => {
    if (recorder.audioBlob && recorder.audioURL && activeStep === 0) {
      setOriginalBlob(recorder.audioBlob)
      setOriginalURL(recorder.audioURL)
    }
  }, [recorder.audioBlob, recorder.audioURL, activeStep])

  // M18: cleanup elapsed timer on unmount
  useEffect(() => {
    return () => {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current)
      }
    }
  }, [])

  const handleFileSelected = useCallback((blob: Blob, url: string) => {
    setOriginalBlob(blob)
    setOriginalURL(url)
  }, [])

  const handleMicClick = useCallback(async () => {
    if (recorder.isRecording) {
      recorder.stopRecording()
    } else {
      setOriginalBlob(null)
      setOriginalURL(null)
      try {
        await recorder.startRecording()
      } catch {
        // mic access denied
      }
    }
  }, [recorder])

  const handleTranscribe = useCallback(async () => {
    if (!originalBlob) return
    setTranscribing(true)
    setTranscribeError(null)

    // M18: start elapsed timer
    setElapsedMs(0)
    const startTime = Date.now()
    elapsedIntervalRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTime)
    }, 100)

    try {
      const token = await auth.getToken()
      const formData = new FormData()
      formData.append('file_channel1', originalBlob, 'recording.webm')
      // M17: use selected language in transcription config
      formData.append('config', JSON.stringify({ lang: transcribeLang, sample_rate: 16000 }))

      const response = await fetch(`${API_BASE}/transcription`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Transcription failed: ${response.status}`)
      }

      const body = await response.json()
      let text: string
      if (body.text) {
        text = body.text
      } else if (Array.isArray(body)) {
        text = body.map((item: any) => item.lexical || item.text || '').join(' ')
      } else {
        text = ''
      }

      setEditedText(text.trim())
      setActiveStep(2)
    } catch (err: any) {
      setTranscribeError(err.message || t('tts.errorGeneric', 'Failed to generate audio'))
    } finally {
      // M18: stop elapsed timer
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current)
        elapsedIntervalRef.current = null
      }
      setTranscribing(false)
    }
  }, [originalBlob, auth, t, transcribeLang])

  const handleSpeak = useCallback(async () => {
    if (!editedText.trim() || !selectedVoiceId) return
    setTtsLoading(true)
    setTtsError(null)
    setTtsBlob(null)

    try {
      const token = await auth.getToken()
      const result = await synthesize(selectedVoiceId, { text: editedText, format: 'wav' }, token)
      setTtsBlob(result.audioBlob)
    } catch (err: any) {
      setTtsError(err.message || t('tts.errorGeneric', 'Failed to generate audio'))
    } finally {
      setTtsLoading(false)
    }
  }, [editedText, selectedVoiceId, auth, t])

  const handleReset = useCallback(() => {
    setActiveStep(0)
    setOriginalBlob(null)
    setOriginalURL(null)
    setTranscribeError(null)
    setEditedText('')
    setTtsBlob(null)
    setTtsError(null)
    setElapsedMs(0)
    recorder.reset()
  }, [recorder])

  const grouped = groupVoicesByLanguage(voices)
  const languageKeys = Object.keys(grouped).sort()

  // Step color for stepper
  const stepColor = activeStep <= 1 ? (isDark ? '#00CFF3' : '#007d8a') : '#004a57'

  // M18: format elapsed time as X.Xs
  const elapsedSeconds = (elapsedMs / 1000).toFixed(1)

  return (
    <Fade in timeout={300}>
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: { xs: 1.5, md: 2 },
      }}>
        {/* Header Card -- matches ASR/TTS form style */}
        <Box
          sx={{
            padding: { xs: 2, md: 3 },
            borderRadius: '16px',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'background.paper',
            backdropFilter: 'blur(10px)',
            boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.06)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid',
            borderColor: isDark ? undefined : 'divider',
            mb: 2,
            flexShrink: 0,
          }}
        >
          {/* Title row */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 2, md: 4 },
            mb: 3,
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #00CFF3, #004a57)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 0.5,
                  fontSize: { xs: '1.8rem', md: '2.125rem' },
                }}
              >
                {t('tts.roundTrip', 'Round-Trip')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 400,
                }}
              >
                {t('tts.roundTripDescription', 'Record speech, transcribe, edit, and re-synthesize')}
              </Typography>
            </Box>

            {/* Reset button -- always visible when past step 0 */}
            {activeStep > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'divider',
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                  '&:hover': {
                    borderColor: isDark ? 'rgba(255,255,255,0.4)' : 'text.secondary',
                    backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                {t('tts.startOver', 'Start Over')}
              </Button>
            )}
          </Box>

          {/* Stepper -- inside header card */}
          <Stepper
            activeStep={activeStep}
            sx={{
              '& .MuiStepLabel-root .Mui-completed': { color: stepColor },
              '& .MuiStepLabel-root .Mui-active': { color: stepColor },
              '& .MuiStepIcon-root.Mui-active': { color: stepColor },
              '& .MuiStepIcon-root.Mui-completed': { color: stepColor },
              '& .MuiStepLabel-label': {
                color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                fontWeight: 500,
              },
              '& .MuiStepLabel-label.Mui-active': {
                color: isDark ? '#fff' : 'text.primary',
                fontWeight: 700,
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: isDark ? 'rgba(255,255,255,0.8)' : 'text.primary',
              },
              '& .MuiStepConnector-line': {
                borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'divider',
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Stage content area */}
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>

          {/* Stage 0: Record / Upload */}
          {activeStep === 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${accentBorder}`,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                overflow: 'hidden',
              }}
            >
              {/* Card header */}
              <Box sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
                background: accentBg,
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? '#fff' : accentColor,
                  }}
                >
                  {t('tts.recordOrUpload', 'Record or Upload Audio')}
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                {/* M17: Language selector */}
                <FormControl size="small" sx={{ mb: 3, minWidth: 220 }}>
                  <InputLabel
                    id="rt-lang-label"
                    sx={{
                      color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                      '&.Mui-focused': { color: isDark ? '#00CFF3' : '#007d8a' },
                    }}
                  >
                    {t('tts.transcriptionLanguage', 'Transcription Language')}
                  </InputLabel>
                  <Select
                    labelId="rt-lang-label"
                    value={transcribeLang}
                    label={t('tts.transcriptionLanguage', 'Transcription Language')}
                    onChange={(e) => setTranscribeLang(e.target.value as string)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'transparent',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'divider',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'text.secondary',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? '#00CFF3' : '#007d8a',
                      },
                      '& .MuiSelect-select': {
                        color: isDark ? 'rgba(255,255,255,0.9)' : 'text.primary',
                      },
                      '& .MuiSvgIcon-root': {
                        color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary',
                      },
                    }}
                  >
                    {LANGUAGES.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Record / Upload area -- centered, spacious */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 3, md: 5 },
                    py: 3,
                    px: 2,
                    borderRadius: 3,
                    border: `1px dashed ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Mic button zone */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                  }}>
                    <IconButton
                      onClick={handleMicClick}
                      size="large"
                      aria-label={recorder.isRecording ? t('tts.stopStreaming', 'Stop') : t('tts.recordAudio', 'Record')}
                      aria-pressed={recorder.isRecording}
                      sx={{
                        width: 80,
                        height: 80,
                        backgroundColor: recorder.isRecording
                          ? (isDark ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)')
                          : (isDark ? 'rgba(0, 207, 243, 0.15)' : 'rgba(0, 125, 138, 0.1)'),
                        color: recorder.isRecording
                          ? '#f44336'
                          : (isDark ? '#00CFF3' : '#007d8a'),
                        border: `2px solid ${recorder.isRecording
                          ? 'rgba(244, 67, 54, 0.3)'
                          : (isDark ? 'rgba(0, 207, 243, 0.25)' : 'rgba(0, 125, 138, 0.2)')
                        }`,
                        '&:hover': {
                          backgroundColor: recorder.isRecording
                            ? (isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.18)')
                            : (isDark ? 'rgba(0, 207, 243, 0.25)' : 'rgba(0, 125, 138, 0.18)'),
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease',
                        ...(recorder.isRecording && {
                          animation: 'pulse 1.2s infinite',
                          '@keyframes pulse': {
                            '0%': { boxShadow: '0 0 0 0 rgba(244,67,54,0.4)' },
                            '70%': { boxShadow: '0 0 0 12px rgba(244,67,54,0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(244,67,54,0)' },
                          },
                        }),
                      }}
                    >
                      {recorder.isRecording ? <StopIcon fontSize="large" /> : <MicIcon fontSize="large" />}
                    </IconButton>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: recorder.isRecording
                          ? '#f44336'
                          : (isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary'),
                      }}
                    >
                      {recorder.isRecording
                        ? t('tts.stopStreaming', 'Stop')
                        : t('tts.recordAudio', 'Record')}
                    </Typography>
                  </Box>

                  {/* Divider */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Box sx={{
                      width: 1,
                      height: 28,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                      display: { xs: 'none', sm: 'block' },
                    }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? 'rgba(255,255,255,0.35)' : 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontSize: '0.65rem',
                      }}
                    >
                      {t('tts.or', 'or')}
                    </Typography>
                    <Box sx={{
                      width: 1,
                      height: 28,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                      display: { xs: 'none', sm: 'block' },
                    }} />
                  </Box>

                  {/* Upload zone */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                  }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                        border: `2px dashed ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                      }}
                    >
                      <FileUploader onFileSelected={handleFileSelected} />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      }}
                    >
                      {t('tts.uploadFile', 'Upload File')}
                    </Typography>
                  </Box>
                </Box>

                {/* Preview captured audio */}
                {originalURL && (
                  <Fade in timeout={300}>
                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          fontSize: '0.7rem',
                          mb: 1,
                          display: 'block',
                        }}
                      >
                        {t('tts.originalRecording', 'Original Recording')}
                      </Typography>
                      <audio
                        controls
                        src={originalURL}
                        style={{ width: '100%', borderRadius: 8 }}
                        title={t('tts.originalRecording', 'Original Recording')}
                      />
                    </Box>
                  </Fade>
                )}

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    disabled={!originalBlob || recorder.isRecording}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => setActiveStep(1)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      background: originalBlob && !recorder.isRecording
                        ? (isDark ? 'linear-gradient(45deg, #00CFF3, #00A8C6)' : 'linear-gradient(45deg, #007d8a, #00697a)')
                        : undefined,
                      '&:hover': {
                        background: isDark ? 'linear-gradient(45deg, #00A8C6, #008FA5)' : 'linear-gradient(45deg, #00697a, #005a6a)',
                      },
                      '&.Mui-disabled': {
                        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    {t('tts.next', 'Next')}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Stage 1: Transcribe */}
          {activeStep === 1 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${accentBorder}`,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                overflow: 'hidden',
              }}
            >
              {/* Card header */}
              <Box sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
                background: accentBg,
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? '#fff' : accentColor,
                  }}
                >
                  {t('tts.step2Transcribe', 'Transcribe')}
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                {transcribing ? (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    py: 4,
                  }}>
                    <CircularProgress
                      size={40}
                      sx={{ color: isDark ? '#00CFF3' : '#007d8a' }}
                    />
                    {/* M18: elapsed timer display */}
                    <Typography
                      variant="body1"
                      sx={{
                        color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      {t('tts.transcribing', 'Transcribing...')} ({elapsedSeconds}s)
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {transcribeError && (
                      <Alert
                        severity="error"
                        sx={{ mb: 2, borderRadius: 2 }}
                        onClose={() => setTranscribeError(null)}
                      >
                        {transcribeError}
                      </Alert>
                    )}

                    {originalURL && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            fontSize: '0.7rem',
                            mb: 1,
                            display: 'block',
                          }}
                        >
                          {t('tts.originalRecording', 'Original Recording')}
                        </Typography>
                        <audio
                          controls
                          src={originalURL}
                          style={{ width: '100%', borderRadius: 8 }}
                          title={t('tts.originalRecording', 'Original Recording')}
                        />
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setActiveStep(0)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'divider',
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                          '&:hover': {
                            borderColor: isDark ? 'rgba(255,255,255,0.4)' : 'text.secondary',
                            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                          },
                        }}
                      >
                        {t('tts.back', 'Back')}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleTranscribe}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                          background: isDark
                            ? 'linear-gradient(45deg, #00CFF3, #00A8C6)'
                            : 'linear-gradient(45deg, #007d8a, #00697a)',
                          '&:hover': {
                            background: isDark
                              ? 'linear-gradient(45deg, #00A8C6, #008FA5)'
                              : 'linear-gradient(45deg, #00697a, #005a6a)',
                          },
                        }}
                      >
                        {t('tts.step2Transcribe', 'Transcribe')}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
          )}

          {/* Stage 2: Edit */}
          {activeStep === 2 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${accentBorder}`,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                overflow: 'hidden',
              }}
            >
              {/* Card header */}
              <Box sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
                background: accentBg,
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? '#fff' : accentColor,
                  }}
                >
                  {t('tts.editTranscription', 'Edit Transcription')}
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                <TextField
                  multiline
                  minRows={4}
                  maxRows={10}
                  fullWidth
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  placeholder={t('tts.textPlaceholder', 'Enter text here...')}
                  inputProps={{ 'aria-label': t('tts.editTranscription', 'Edit Transcription') }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#004a57',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: isDark ? 'rgba(255,255,255,0.9)' : 'text.primary',
                    },
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setActiveStep(1)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'divider',
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      '&:hover': {
                        borderColor: isDark ? 'rgba(255,255,255,0.4)' : 'text.secondary',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    {t('tts.back', 'Back')}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!editedText.trim()}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => setActiveStep(3)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      background: editedText.trim()
                        ? 'linear-gradient(45deg, #004a57, #00758a)'
                        : undefined,
                      '&:hover': {
                        background: 'linear-gradient(45deg, #6A3DE8, #A070FF)',
                      },
                      '&.Mui-disabled': {
                        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    {t('tts.proceedToTts', 'Proceed to TTS')}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Stage 3: Speak */}
          {activeStep === 3 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${accentBorder}`,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                overflow: 'hidden',
              }}
            >
              {/* Card header */}
              <Box sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderBottomColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
                background: accentBg,
              }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? '#fff' : accentColor,
                  }}
                >
                  {t('tts.step4Speak', 'Speak')}
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                {/* Voice selector */}
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                  <InputLabel
                    id="rt-voice-label"
                    sx={{
                      color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                      '&.Mui-focused': { color: '#004a57' },
                    }}
                  >
                    {t('tts.voiceLabel', 'Voice')}
                  </InputLabel>
                  <Select
                    labelId="rt-voice-label"
                    value={selectedVoiceId}
                    label={t('tts.voiceLabel', 'Voice')}
                    onChange={(e) => setSelectedVoiceId(e.target.value as string)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'transparent',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'divider',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'text.secondary',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#004a57',
                      },
                      '& .MuiSelect-select': {
                        color: isDark ? 'rgba(255,255,255,0.9)' : 'text.primary',
                      },
                      '& .MuiSvgIcon-root': {
                        color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary',
                      },
                    }}
                  >
                    {languageKeys.map((lang) => [
                      <ListSubheader
                        key={`rt-lang-${lang}`}
                        sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                      >
                        {lang}
                      </ListSubheader>,
                      ...grouped[lang].map((voice) => (
                        <MenuItem key={voice.voice_id} value={voice.voice_id} sx={{ pl: 3 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {voice.name}
                            </Typography>
                            {voice.description && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {voice.description}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      )),
                    ])}
                  </Select>
                </FormControl>

                {ttsError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2, borderRadius: 2 }}
                    onClose={() => setTtsError(null)}
                  >
                    {ttsError}
                  </Alert>
                )}

                {ttsBlob && (
                  <Box sx={{ mb: 3 }}>
                    <TtsAudioPlayer audioBlob={ttsBlob} fileName="tts-output.wav" />
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setActiveStep(2)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'divider',
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      '&:hover': {
                        borderColor: isDark ? 'rgba(255,255,255,0.4)' : 'text.secondary',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    {t('tts.back', 'Back')}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!selectedVoiceId || !editedText.trim() || ttsLoading}
                    onClick={handleSpeak}
                    startIcon={ttsLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      background:
                        selectedVoiceId && editedText.trim() && !ttsLoading
                          ? 'linear-gradient(45deg, #004a57, #00758a)'
                          : undefined,
                      '&:hover': {
                        background: 'linear-gradient(45deg, #6A3DE8, #A070FF)',
                      },
                      '&.Mui-disabled': {
                        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    {ttsLoading ? t('tts.generating', 'Generating...') : t('tts.speakIt', 'Speak it')}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Comparison view: visible after TTS completes */}
          {ttsBlob && activeStep === 3 && (
            <Fade in timeout={400}>
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? 'rgba(255,255,255,0.9)' : tokens.colors.tts.main,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.7rem',
                    mb: 2,
                  }}
                >
                  {t('tts.compareAudio', 'Compare Audio')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* Original */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      minWidth: 260,
                      p: 2.5,
                      borderRadius: 3,
                      border: `1px solid ${isDark ? 'rgba(0, 207, 243, 0.2)' : 'rgba(0, 125, 138, 0.18)'}`,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'background.paper',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? '#00CFF3' : tokens.colors.primary.darkText,
                        mb: 1.5,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {t('tts.originalRecording', 'Original Recording')}
                    </Typography>
                    {originalURL && (
                      <audio
                        controls
                        src={originalURL}
                        style={{ width: '100%', borderRadius: 8 }}
                        title={t('tts.originalRecording', 'Original Recording')}
                      />
                    )}
                  </Paper>

                  {/* TTS Output */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      minWidth: 260,
                      p: 2.5,
                      borderRadius: 3,
                      border: `1px solid ${isDark ? 'rgba(0, 74, 87, 0.2)' : 'rgba(0, 74, 87, 0.18)'}`,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'background.paper',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
                        mb: 1.5,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {t('tts.ttsOutput', 'TTS Output')}
                    </Typography>
                    <TtsAudioPlayer audioBlob={ttsBlob} fileName="tts-output.wav" />
                  </Paper>
                </Box>

                {/* Reset button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleReset}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: isDark ? 'rgba(0, 74, 87, 0.3)' : 'rgba(0, 74, 87, 0.4)',
                      color: '#004a57',
                      '&:hover': {
                        borderColor: '#004a57',
                        backgroundColor: isDark ? 'rgba(0, 74, 87, 0.1)' : 'rgba(0, 74, 87, 0.06)',
                      },
                    }}
                  >
                    {t('tts.startOver', 'Start Over')}
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Fade>
  )
}

export default RoundTripDemo
